import {
  diagnoseQzProfile,
  normalizePrintInteger,
  parsePdfPageRanges,
  QzPrintError,
  sanitizeQzProfileSettings,
  validateHtmlPrintJobs,
  validatePdfBase64,
  withPrintTimeout,
} from './qzPrintValidation';
import { expandHtmlJobsForQzPages } from '../utils/qzHtmlPageSplit';
import {
  groupConsecutiveQzHtmlSpools,
  qzHtmlSpoolFingerprint,
  qzHtmlSpoolTimeoutMs,
  qzInterSpoolGapMs,
} from '../utils/qzHtmlSpoolGroups';

export const QZ_TARGET_PRINTER = 'Brother MFC-J6955DW Printer';
export const QZ_SHIPPING_LABEL_PRINTER = 'Godex RT730i';
export const QZ_SETTINGS_STORAGE_KEY = 'lsc.qzPrintSettings.v6';

export const QZ_PRINT_PROFILES = Object.freeze({
  standard: Object.freeze({
    key: 'standard',
    label: 'Standard A4',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Tray 1',
    size: Object.freeze({ width: 210, height: 297 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a4',
  }),
  cheetah: Object.freeze({
    key: 'cheetah',
    label: 'Cheetah LZ17',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Tray 2',
    size: Object.freeze({ width: 210, height: 297 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a4',
  }),
  separator: Object.freeze({
    key: 'separator',
    label: 'A4 separator sheet',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Tray 1',
    size: Object.freeze({ width: 210, height: 297 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a4',
  }),
  giftCard: Object.freeze({
    key: 'giftCard',
    label: 'A6 inscription card',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Multipurpose tray',
    size: Object.freeze({ width: 105, height: 148 }),
    orientation: 'reverse-landscape',
    rotation: 180,
    density: 300,
    paperSizeName: 'iso-a6',
  }),
  shippingLabel: Object.freeze({
    key: 'shippingLabel',
    label: 'Shipping label PDF',
    printer: QZ_SHIPPING_LABEL_PRINTER,
    printerTray: 'Continuous Roll',
    size: Object.freeze({ width: 100.076, height: 150.114 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: '3.94 x 5.91',
  }),
  /** GO4 outside HTML on the same GoDEX stock as carrier PDFs (HTML raster, not vector PDF). */
  go4Outside: Object.freeze({
    key: 'go4Outside',
    label: 'GO4 GoDEX outside',
    printer: QZ_SHIPPING_LABEL_PRINTER,
    printerTray: 'Continuous Roll',
    size: Object.freeze({ width: 100.076, height: 150.114 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: '3.94 x 5.91',
  }),
  generalA6: Object.freeze({
    key: 'generalA6',
    label: 'General A6',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Multipurpose tray',
    size: Object.freeze({ width: 105, height: 148 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a6',
  }),
  generalA4: Object.freeze({
    key: 'generalA4',
    label: 'General A4',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Tray 1',
    size: Object.freeze({ width: 210, height: 297 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a4',
  }),
  generalA3: Object.freeze({
    key: 'generalA3',
    label: 'General A3',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Tray 1',
    size: Object.freeze({ width: 297, height: 420 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-a3',
  }),
  generalC5: Object.freeze({
    key: 'generalC5',
    label: 'General C5',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Multipurpose tray',
    size: Object.freeze({ width: 162, height: 229 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-c5',
  }),
  generalC6: Object.freeze({
    key: 'generalC6',
    label: 'General C6',
    printer: QZ_TARGET_PRINTER,
    printerTray: 'Multipurpose tray',
    size: Object.freeze({ width: 114, height: 162 }),
    orientation: 'portrait',
    density: 300,
    paperSizeName: 'iso-c6',
  }),
});

let securityConfigured = false;
let securityMode = 'unsigned';
let securityLastAttempt = 0;
let connectPromise = null;
let readinessCache = null;
let readinessCachedAt = 0;
let qzClientPromise = null;
let securityWarning = '';
let settingsStorageWarning = '';
let qzPrintLock = Promise.resolve();

function withQzPrintLock(operation) {
  const run = qzPrintLock.catch(() => {}).then(operation);
  qzPrintLock = run.catch(() => {});
  return run;
}

export function resetQzPrintReadinessCache() {
  readinessCache = null;
  readinessCachedAt = 0;
}

async function getQzClient() {
  if (!qzClientPromise) {
    qzClientPromise = import('qz-tray')
      .then((module) => module.default || module)
      .catch((error) => {
        qzClientPromise = null;
        throw new QzPrintError('QZ Tray support could not be loaded. Use browser printing.', {
          code: 'QZ_LIBRARY_UNAVAILABLE',
          phase: 'connection',
          cause: error,
        });
      });
  }
  return qzClientPromise;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 2500) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller?.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function configureSecurity(qz) {
  if (securityConfigured) return securityMode;
  if (Date.now() - securityLastAttempt < 10000) return securityMode;
  securityLastAttempt = Date.now();
  const certificateUrl =
    process.env.REACT_APP_QZ_CERT_URL || 'http://127.0.0.1:9191/certificate';
  const signingUrl =
    process.env.REACT_APP_QZ_SIGN_URL || 'http://127.0.0.1:9191/sign';
  if (
    process.env.NODE_ENV === 'test' &&
    !process.env.REACT_APP_QZ_CERT_URL &&
    !process.env.REACT_APP_QZ_SIGN_URL
  ) {
    return securityMode;
  }
  try {
    const certificateResponse = await fetchWithTimeout(certificateUrl, {
      cache: 'no-store',
      headers: { 'Content-Type': 'text/plain' },
    });
    if (!certificateResponse.ok) throw new Error('QZ certificate unavailable');
    const certificate = await certificateResponse.text();
    const healthUrl = new URL('/health', certificateUrl).toString();
    const healthResponse = await fetchWithTimeout(healthUrl, { cache: 'no-store' });
    if (!healthResponse.ok) throw new Error('QZ signer health check failed');
    qz.security.setCertificatePromise((resolve) => resolve(certificate));
  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    fetchWithTimeout(signingUrl, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request: toSign }),
    }, 5000)
      .then((response) =>
        response.ok ? response.text() : Promise.reject(new Error('QZ signing failed')),
      )
      .then(resolve)
      .catch(reject);
  });
    securityConfigured = true;
    securityMode = 'signed';
    securityWarning = '';
  } catch (error) {
    securityMode = 'unsigned';
    securityWarning =
      error?.name === 'AbortError'
        ? 'Local QZ signer timed out; unsigned approval mode will be used.'
        : 'Local QZ signer is unavailable; unsigned approval mode will be used.';
  }
  return securityMode;
}

const asArray = (value) =>
  Array.isArray(value) ? value : value == null ? [] : [value];

const sizeDimensions = (size) => {
  const mm = size?.millimeters || size?.mm;
  if (mm?.width && mm?.height) {
    return { width: Number(mm.width), height: Number(mm.height) };
  }
  if (size?.width && size?.height) {
    return { width: Number(size.width), height: Number(size.height) };
  }
  return null;
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export function normalizeQzPrinterDetail(detail) {
  const sizes = asArray(detail?.sizes).map((size) => {
    if (typeof size === 'string') return { name: size, size: null };
    return {
      name: String(size?.name || size?.choiceName || size?.media || 'Custom size'),
      size: sizeDimensions(size),
    };
  });
  const densities = [
    ...asArray(detail?.density),
    ...asArray(detail?.densities),
  ]
    .map((density) => Number(density?.dpi || density?.cross || density?.feed || density))
    .filter((density) => Number.isFinite(density) && density > 0);
  return {
    ...detail,
    name: String(detail?.name || ''),
    driver: String(detail?.driver || ''),
    connection: String(detail?.connection || detail?.port || ''),
    trays: asArray(detail?.trays).map(String),
    sizes,
    densities: Array.from(new Set(densities)).sort((a, b) => a - b),
    capabilitiesAvailable:
      detail?.capabilitiesAvailable !== false &&
      (Object.prototype.hasOwnProperty.call(detail || {}, 'trays') ||
        Object.prototype.hasOwnProperty.call(detail || {}, 'sizes') ||
        Object.prototype.hasOwnProperty.call(detail || {}, 'density') ||
        Object.prototype.hasOwnProperty.call(detail || {}, 'densities')),
  };
}

export function buildQzProfileConfig(profileKey, overrides = {}, printerDetail = null) {
  const profile = QZ_PRINT_PROFILES[profileKey];
  if (!profile) throw new Error(`Unknown QZ print profile: ${profileKey}`);
  const safeOverrides = sanitizeQzProfileSettings(
    overrides,
    getDefaultQzSettings(profileKey),
  ).value;
  const selectedSize = printerDetail?.sizes?.find(
    (size) => size.name === safeOverrides.paperSizeName,
  )?.size;
  const size = safeOverrides.size || selectedSize || profile.size;
  const marginsMm = safeOverrides.margins;
  return {
    units: 'in',
    size: {
      width: Number(size.width) / 25.4,
      height: Number(size.height) / 25.4,
    },
    orientation: safeOverrides.orientation,
    printerTray:
      safeOverrides.printerTray === '' || safeOverrides.printerTray == null
        ? profile.printerTray
        : safeOverrides.printerTray,
    density: safeOverrides.density,
    margins: marginsMm / 25.4,
    copies: 1,
    scaleContent: true,
    colorType: safeOverrides.colorType,
    duplex: safeOverrides.duplex,
    // A6 multipurpose tray is loaded flipped; keep inscription cards at 180° always.
    rotation:
      profile.rotation != null && Number.isFinite(Number(profile.rotation))
        ? Number(profile.rotation)
        : safeOverrides.rotation,
    rasterize: true,
    jobName: String(overrides.jobName || `London Smoke & Cure - ${profile.label}`),
  };
}

export function getDefaultQzSettings(profileKey) {
  const profile = QZ_PRINT_PROFILES[profileKey];
  if (!profile) throw new Error(`Unknown QZ print profile: ${profileKey}`);
  return {
    printer: profile.printer,
    printerTray: profile.printerTray,
    paperSizeName: profile.paperSizeName,
    size: { ...profile.size },
    orientation: profile.orientation,
    rotation: Number.isFinite(Number(profile.rotation)) ? Number(profile.rotation) : 0,
    density: profile.density,
    colorType: 'default',
    duplex: 'one-sided',
    margins: 0,
  };
}

/** Ensure A6 inscription routing always matches the physical multipurpose tray. */
export function withGiftCardTrayRotation(settings = {}) {
  return {
    ...getDefaultQzSettings('giftCard'),
    ...(settings && typeof settings === 'object' ? settings : {}),
    rotation: 180,
    orientation: 'reverse-landscape',
  };
}

export function buildQzTestPageHtml(profileKey, settings = {}) {
  const profile = QZ_PRINT_PROFILES[profileKey];
  if (!profile) throw new Error(`Unknown QZ print profile: ${profileKey}`);
  const value = { ...getDefaultQzSettings(profileKey), ...settings };
  const width = Number(value.size?.width) || profile.size.width;
  const height = Number(value.size?.height) || profile.size.height;
  const landscape = /landscape/.test(value.orientation);
  const pageWidth = landscape ? Math.max(width, height) : Math.min(width, height);
  const pageHeight = landscape ? Math.min(width, height) : Math.max(width, height);
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>London Smoke &amp; Cure QZ test</title>
  <style>
    @page { size: ${pageWidth}mm ${pageHeight}mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body { width: ${pageWidth}mm; height: ${pageHeight}mm; margin: 0; }
    body { padding: 10mm; font-family: Arial, Helvetica, sans-serif; color: #172220; background: #fff; }
    main { width: 100%; height: 100%; padding: 8mm; border: 1.2mm solid #172220; display: flex; flex-direction: column; }
    .brand { color: #8c6828; font-size: 10pt; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
    h1 { margin: 8mm 0 3mm; font-size: 24pt; line-height: 1; }
    p { margin: 2mm 0; font-size: 11pt; }
    dl { display: grid; grid-template-columns: 34mm 1fr; margin: 7mm 0 0; font-size: 10pt; }
    dt, dd { margin: 0; padding: 2.5mm 1mm; border-bottom: 0.25mm solid #c7cecb; }
    dt { color: #596561; font-weight: 700; }
    dd { font-weight: 800; }
    .footer { margin-top: auto; font-size: 8pt; color: #596561; }
  </style>
</head>
<body>
  <main>
    <div class="brand">London Smoke &amp; Cure</div>
    <h1>QZ print test</h1>
    <p>If this page is correctly oriented and sourced, this profile is ready.</p>
    <dl>
      <dt>Profile</dt><dd>${escapeHtml(profile.label)}</dd>
      <dt>Printer</dt><dd>${escapeHtml(value.printer)}</dd>
      <dt>Paper source</dt><dd>${escapeHtml(value.printerTray)}</dd>
      <dt>Paper</dt><dd>${escapeHtml(value.paperSizeName)} (${width} x ${height} mm)</dd>
      <dt>Orientation</dt><dd>${escapeHtml(value.orientation)}</dd>
      <dt>Rotation</dt><dd>${escapeHtml(value.rotation)} degrees</dd>
      <dt>Density</dt><dd>${escapeHtml(value.density)} dpi</dd>
      <dt>Colour</dt><dd>${escapeHtml(value.colorType)}</dd>
      <dt>Sides</dt><dd>${escapeHtml(value.duplex)}</dd>
    </dl>
    <p class="footer">Generated ${escapeHtml(new Date().toLocaleString('en-GB'))}</p>
  </main>
</body>
</html>`;
}

export function loadQzPrintSettings() {
  const defaults = Object.keys(QZ_PRINT_PROFILES).reduce((map, key) => {
    map[key] = getDefaultQzSettings(key);
    return map;
  }, {});
  if (typeof localStorage === 'undefined') return defaults;
  try {
    const stored = JSON.parse(localStorage.getItem(QZ_SETTINGS_STORAGE_KEY) || '{}');
    Object.keys(defaults).forEach((key) => {
      const sanitized = sanitizeQzProfileSettings(stored[key], defaults[key]);
      defaults[key] = sanitized.value;
      if (sanitized.warnings.length) {
        settingsStorageWarning = 'Some saved printer settings were invalid and were repaired.';
      }
    });
    defaults.giftCard = withGiftCardTrayRotation(defaults.giftCard);
  } catch {
    settingsStorageWarning = 'Saved printer settings could not be read; safe defaults are in use.';
  }
  return defaults;
}

export function saveQzPrintSettings(settings) {
  if (typeof localStorage === 'undefined') return false;
  try {
    const next = { ...(settings || {}) };
    if (next.giftCard) next.giftCard = withGiftCardTrayRotation(next.giftCard);
    localStorage.setItem(QZ_SETTINGS_STORAGE_KEY, JSON.stringify(next));
    settingsStorageWarning = '';
    return true;
  } catch {
    settingsStorageWarning =
      'Printer settings could not be saved and will apply only for this session.';
    return false;
  }
}

export function reconcileQzPrintSettings(settings, printerDetails) {
  return Object.keys(QZ_PRINT_PROFILES).reduce((next, profileKey) => {
    const sanitized = sanitizeQzProfileSettings(
      settings?.[profileKey],
      getDefaultQzSettings(profileKey),
    );
    next[profileKey] =
      profileKey === 'giftCard'
        ? withGiftCardTrayRotation(sanitized.value)
        : sanitized.value;
    return next;
  }, {});
}

export function diagnoseQzProfiles(settings, printerDetails, profileKeys) {
  return (profileKeys || Object.keys(QZ_PRINT_PROFILES)).map((profileKey) =>
    diagnoseQzProfile(
      profileKey,
      settings?.[profileKey] || getDefaultQzSettings(profileKey),
      printerDetails,
    ),
  );
}

export async function connectQzTray() {
  const qz = await getQzClient();
  await configureSecurity(qz);
  if (qz.websocket.isActive()) return true;
  if (!connectPromise) {
    connectPromise = withPrintTimeout(
      qz.websocket.connect({
        retries: 2,
        delay: 1,
        port: {
          secure: [8282, 8181, 8383, 8484],
          insecure: [8283, 8182, 8384, 8485],
        },
      }),
      12000,
      'QZ Tray did not respond. Start QZ Tray, then recheck the connection.',
      { code: 'QZ_CONNECTION_TIMEOUT', phase: 'connection' },
    ).finally(() => {
        connectPromise = null;
      });
  }
  await connectPromise;
  if (!qz.websocket.isActive()) {
    throw new QzPrintError('QZ Tray connected but did not become active.', {
      code: 'QZ_INACTIVE',
      phase: 'connection',
    });
  }
  return true;
}

export async function getQzPrintReadiness({ force = false } = {}) {
  let qz;
  try {
    qz = await getQzClient();
  } catch (error) {
    return {
      ready: false,
      connected: false,
      printer: null,
      printers: [],
      printerDetails: [],
      signed: false,
      warnings: [],
      errorCode: error?.code || 'QZ_LIBRARY_UNAVAILABLE',
      message: error?.message || 'QZ Tray support could not be loaded.',
    };
  }
  if (
    !force &&
    readinessCache &&
    Date.now() - readinessCachedAt < 10000 &&
    qz.websocket.isActive()
  ) {
    return readinessCache;
  }
  try {
    await connectQzTray();
    let printerDetails;
    let discoveryWarning = '';
    try {
      const details = await withPrintTimeout(
        qz.printers.details(),
        8000,
        'Printer capability discovery timed out.',
        { code: 'QZ_DISCOVERY_TIMEOUT', phase: 'discovery' },
      );
      printerDetails = asArray(details).map(normalizeQzPrinterDetail);
    } catch (detailsError) {
      const printerNames = await withPrintTimeout(
        qz.printers.find(),
        8000,
        'Printer discovery timed out.',
        { code: 'QZ_DISCOVERY_TIMEOUT', phase: 'discovery' },
      );
      printerDetails = asArray(printerNames).map((name) =>
        normalizeQzPrinterDetail({ name, capabilitiesAvailable: false }),
      );
      discoveryWarning =
        'QZ found printers but detailed trays, media, and driver capabilities are unavailable.';
    }
    const exact = printerDetails.find((item) => item.name === QZ_TARGET_PRINTER);
    const insensitive = printerDetails.find(
      (item) => item.name.toLowerCase() === QZ_TARGET_PRINTER.toLowerCase(),
    );
    const printer = exact?.name || insensitive?.name || printerDetails[0]?.name || null;
    const result = {
      ready: printerDetails.length > 0,
      connected: true,
      printer,
      printers: printerDetails.map((item) => item.name),
      printerDetails,
      signed: securityMode === 'signed',
      securityWarning,
      storageWarning: settingsStorageWarning,
      discoveryWarning,
      warnings: [securityWarning, settingsStorageWarning, discoveryWarning].filter(Boolean),
      message: printerDetails.length
        ? `QZ ready: ${printerDetails.length} printer${
            printerDetails.length === 1 ? '' : 's'
          } available.`
        : 'QZ is connected but no printers were found.',
    };
    readinessCache = result;
    readinessCachedAt = Date.now();
    return result;
  } catch (error) {
    readinessCache = null;
    readinessCachedAt = 0;
    return {
      ready: false,
      connected: false,
      printer: null,
      printers: [],
      printerDetails: [],
      signed: false,
      securityWarning,
      storageWarning: settingsStorageWarning,
      discoveryWarning: '',
      warnings: [securityWarning, settingsStorageWarning].filter(Boolean),
      errorCode: error?.code || 'QZ_UNAVAILABLE',
      message: error?.message || 'QZ Tray is not available.',
    };
  }
}

export async function printHtmlWithQz(profileKey, html, overrides = {}) {
  const [result] = await printHtmlBatchWithQz([
    { profileKey, html, settings: overrides },
  ]);
  return result;
}

export async function printHtmlBatchWithQz(jobs = []) {
  const queue = expandHtmlJobsForQzPages(
    validateHtmlPrintJobs(jobs, QZ_PRINT_PROFILES),
  );
  const readiness = await getQzPrintReadiness({ force: false });
  if (!readiness.ready) throw new Error(readiness.message);
  const qz = await getQzClient();
  const pages = [];
  queue.forEach((job) => {
    const { profileKey, html } = job;
    const overrides =
      profileKey === 'giftCard'
        ? withGiftCardTrayRotation(job.settings)
        : job.settings || {};
    const profile = QZ_PRINT_PROFILES[profileKey];
    const printerName = overrides.printer || profile.printer || readiness.printer;
    const printerDetail = readiness.printerDetails.find(
      (item) => item.name === printerName,
    );
    if (!printerDetail) {
      throw new QzPrintError(`QZ printer "${printerName}" is not currently available.`, {
        code: 'QZ_PRINTER_UNAVAILABLE',
        phase: 'preflight',
        pending: queue,
      });
    }
    const configOptions = buildQzProfileConfig(profileKey, overrides, printerDetail);
    let config;
    try {
      config = qz.configs.create(printerName, configOptions);
    } catch (error) {
      throw new QzPrintError(
        `QZ could not configure ${profile.label} for ${printerName}.`,
        {
          code: 'QZ_CONFIG_FAILED',
          phase: 'preflight',
          pending: queue,
          cause: error,
        },
      );
    }
    const isLandscape = /landscape/.test(configOptions.orientation);
    const pageWidth = isLandscape
      ? Math.max(configOptions.size.width, configOptions.size.height)
      : Math.min(configOptions.size.width, configOptions.size.height);
    const pageHeight = isLandscape
      ? Math.min(configOptions.size.width, configOptions.size.height)
      : Math.max(configOptions.size.width, configOptions.size.height);
    pages.push({
      fingerprint: qzHtmlSpoolFingerprint(printerName, configOptions),
      printer: printerName,
      config,
      job,
      payload: {
        type: 'pixel',
        format: 'html',
        flavor: 'plain',
        data: String(html || ''),
        options: {
          pageWidth,
          pageHeight,
        },
      },
      result: {
        id: job.id,
        sequence: job.sequence,
        label: job.label,
        profile: profileKey,
        printer: printerName,
        tray: overrides.printerTray || profile.printerTray,
        status: 'submitted',
      },
    });
  });
  const spools = groupConsecutiveQzHtmlSpools(pages);
  const submitted = [];
  for (let index = 0; index < spools.length; index += 1) {
    const spool = spools[index];
    try {
      await withPrintTimeout(
        withQzPrintLock(() => qz.print(spool.config, spool.payloads)),
        qzHtmlSpoolTimeoutMs(spool.payloads.length),
        `QZ did not confirm submission of ${spool.results[0].label}.`,
        {
          code: 'QZ_SUBMISSION_TIMEOUT',
          phase: 'submission',
          submissionStarted: true,
        },
      );
    } catch (error) {
      readinessCache = null;
      throw new QzPrintError(
        `${spool.results[0].label} has an uncertain QZ outcome. Do not retry the full job.`,
        {
          code: error?.code || 'QZ_PARTIAL_SUBMISSION',
          phase: 'submission',
          submissionStarted: true,
          submitted,
          uncertain: spool.jobs.map((job, jobIndex) => ({
            ...job,
            route: spool.results[jobIndex],
          })),
          pending: spools
            .slice(index + 1)
            .flatMap((entry) => entry.jobs),
          cause: error,
        },
      );
    }
    submitted.push(...spool.results);
    if (index < spools.length - 1) {
      const gapMs = qzInterSpoolGapMs(spool.printer, spools[index + 1].printer);
      if (gapMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, gapMs));
      }
    }
  }
  return submitted;
}

export async function printPdfWithQz(
  profileKey,
  pdfBase64,
  overrides = {},
  options = {},
) {
  const profile = QZ_PRINT_PROFILES[profileKey];
  if (!profile) throw new Error(`Unknown QZ print profile: ${profileKey}`);
  const data = validatePdfBase64(pdfBase64);
  const pageCount = normalizePrintInteger(options.pageCount, 1, 10000);
  const parsedRanges = parsePdfPageRanges(options.pageRanges, pageCount);
  const readiness = await getQzPrintReadiness({ force: true });
  if (!readiness.ready) throw new Error(readiness.message);
  const qz = await getQzClient();
  const printerName = overrides.printer || profile.printer || readiness.printer;
  const printerDetail = readiness.printerDetails.find(
    (item) => item.name === printerName,
  );
  if (!printerDetail) {
    throw new Error(`QZ printer "${printerName}" is not currently available.`);
  }
  const configOptions = buildQzProfileConfig(
    profileKey,
    overrides,
    printerDetail,
  );
  configOptions.copies = normalizePrintInteger(options.copies, 1, 99);
  configOptions.rasterize = false;
  configOptions.density = 0;
  const isLandscape = /landscape/.test(configOptions.orientation);
  const pageWidth = isLandscape
    ? Math.max(configOptions.size.width, configOptions.size.height)
    : Math.min(configOptions.size.width, configOptions.size.height);
  const pageHeight = isLandscape
    ? Math.min(configOptions.size.width, configOptions.size.height)
    : Math.max(configOptions.size.width, configOptions.size.height);
  try {
    await withPrintTimeout(
      withQzPrintLock(() =>
        qz.print(qz.configs.create(printerName, configOptions), [
          {
            type: 'pixel',
            format: 'pdf',
            flavor: 'base64',
            data,
            options: {
              pageWidth,
              pageHeight,
              pageRanges: parsedRanges.text || undefined,
              ignoreTransparency: false,
              altFontRendering: false,
            },
          },
        ]),
      ),
      45000,
      'QZ did not confirm the shipping-label submission.',
      {
        code: 'QZ_SUBMISSION_TIMEOUT',
        phase: 'submission',
        submissionStarted: true,
      },
    );
  } catch (error) {
    readinessCache = null;
    throw new QzPrintError(
      'The shipping-label submission is uncertain. Check the printer before retrying.',
      {
        code: error?.code || 'QZ_SUBMISSION_UNCERTAIN',
        phase: 'submission',
        submissionStarted: true,
        uncertain: [{ label: 'Shipping label PDF', profile: profileKey }],
        cause: error,
      },
    );
  }
  return {
    profile: profileKey,
    printer: printerName,
    tray: overrides.printerTray || profile.printerTray,
    copies: configOptions.copies,
    pages: parsedRanges.pages,
    status: 'submitted',
  };
}

export default {
  connectQzTray,
  diagnoseQzProfiles,
  getQzPrintReadiness,
  getDefaultQzSettings,
  withGiftCardTrayRotation,
  loadQzPrintSettings,
  reconcileQzPrintSettings,
  saveQzPrintSettings,
  resetQzPrintReadinessCache,
  printHtmlBatchWithQz,
  printHtmlWithQz,
  printPdfWithQz,
};
